import type { ReactNode } from "react";
import {
  AppWindow,
  CircleCheck,
  CircleX,
  Clock,
  ExternalLink,
  FlaskConical,
  GitBranch,
  GitCommitHorizontal,
  Server,
  TriangleAlert,
  type LucideIcon,
} from "lucide-react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useQuery } from "@tanstack/react-query";

import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";
import { Separator } from "components/ui/separator";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";
import * as QueryKeys from "data";
import type { DeploymentInfo, E2ERunInfo } from "data/models";
import { fetchDeploymentStatus } from "data/queries-common";
import { cn } from "@/lib/utils";

dayjs.extend(relativeTime);

type Status = "ok" | "warn" | "fail";

const statusTextClass: Record<Status, string> = {
  ok: "text-green-600 dark:text-green-500",
  warn: "text-amber-600 dark:text-amber-500",
  fail: "text-red-600 dark:text-red-500",
};

const statusDotClass: Record<Status, string> = {
  ok: "bg-green-500",
  warn: "bg-amber-500",
  fail: "bg-red-500",
};

const formatDuration = (seconds?: number) => {
  if (seconds === undefined) return null;
  const minutes = Math.floor(seconds / 60);
  return minutes > 0 ? `${minutes}m ${seconds % 60}s` : `${seconds}s`;
};

const deploymentStatus = (deployment: DeploymentInfo): Status =>
  deployment.running_build || !(deployment.is_latest_on_branch ?? true)
    ? "warn"
    : "ok";

const e2eStatus = (run?: E2ERunInfo): Status => {
  if (!run || run.running || run.status === "QUEUED") return "warn";
  return run.status === "SUCCESS" ? "ok" : "fail";
};

function StatusTrigger({
  icon: Icon,
  status,
  label,
  hint,
  children,
}: {
  icon: LucideIcon;
  status: Status;
  label: string;
  hint: string;
  children: ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            aria-label={`${label}: ${hint}`}
            className="h-7 gap-1.5 px-1.5 text-[11px] font-normal"
          >
            <span className="relative">
              <Icon
                className={cn(
                  "size-3.5",
                  status === "ok" ? "text-muted-foreground" : statusTextClass[status],
                )}
              />
              <span
                className={cn(
                  "absolute -right-0.5 -top-0.5 size-1.5 rounded-full ring-2 ring-background",
                  statusDotClass[status],
                )}
              />
            </span>
            {children}
          </Button>
        </PopoverTrigger>
      </TooltipTrigger>
      <TooltipContent side="bottom">
        {label} · {hint}
      </TooltipContent>
    </Tooltip>
  );
}

function DetailRow({
  icon: Icon,
  label,
  children,
}: {
  icon: LucideIcon;
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="grid grid-cols-[14px_60px_1fr] items-start gap-2">
      <Icon className="mt-px size-3.5 text-muted-foreground/70" />
      <span className="text-muted-foreground">{label}</span>
      <span className="min-w-0 break-words font-medium">{children}</span>
    </div>
  );
}

function DeploymentPopover({
  deployment,
  title,
  icon: Icon,
}: {
  deployment: DeploymentInfo;
  title: string;
  icon: LucideIcon;
}) {
  const status = deploymentStatus(deployment);
  const running = deployment.running_build;
  const hint = running
    ? running.queued
      ? `queued · ${running.branch_name}`
      : `deploying · ${running.branch_name}`
    : status === "ok"
      ? `${deployment.branch_name} · up to date`
      : `${deployment.branch_name} · behind branch`;

  return (
    <Popover>
      <StatusTrigger icon={Icon} status={status} label={title} hint={hint}>
        <span className="max-w-28 truncate font-mono">
          {running?.branch_name ?? deployment.branch_name}
        </span>
      </StatusTrigger>
      <PopoverContent align="end" className="w-80 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-2">
            <Icon className="mt-0.5 size-4 text-muted-foreground" />
            <div>
              <p className="text-sm font-semibold">{title}</p>
              <p className={cn("mt-0.5 flex items-center gap-1", statusTextClass[status])}>
                {status === "ok" ? <CircleCheck className="size-3" /> : <TriangleAlert className="size-3" />}
                {running ? (running.queued ? "Queued" : "Deployment in progress") : status === "ok" ? "Up to date" : "Behind branch"}
              </p>
            </div>
          </div>
          {deployment.web_url && (
            <a href={deployment.web_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-muted-foreground hover:text-foreground">
              Build <ExternalLink className="size-3" />
            </a>
          )}
        </div>
        <Separator />
        <div className="space-y-2 text-xs">
          <DetailRow icon={GitBranch} label="Branch"><span className="font-mono">{running?.branch_name ?? deployment.branch_name}</span></DetailRow>
          {running ? (
            <>
              <DetailRow icon={Clock} label="Status">{running.queued ? "Waiting for the previous build" : formatDuration(running.elapsed_seconds) ? `Running for ${formatDuration(running.elapsed_seconds)}` : "Just started"}</DetailRow>
              {running.seconds_remaining !== undefined && <DetailRow icon={Clock} label="ETA">~{formatDuration(running.seconds_remaining)}</DetailRow>}
            </>
          ) : (
            <>
              <DetailRow icon={GitCommitHorizontal} label="Build"><span className="font-mono">#{deployment.build_number}{deployment.commit_hash && ` · ${deployment.commit_hash.slice(0, 7)}`}</span></DetailRow>
              {deployment.finish_date && <DetailRow icon={Clock} label="Deployed">{dayjs(deployment.finish_date, "YYYYMMDDTHHmmssZ").fromNow()}</DetailRow>}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function E2EPopover({ run }: { run?: E2ERunInfo }) {
  const status = e2eStatus(run);
  const label = run?.running || run?.status === "QUEUED" ? "E2E run in progress" : run?.status ?? "No E2E run";
  const hint = run ? `${run.status} · ${run.branch_name}` : "No run found";

  return (
    <Popover>
      <StatusTrigger icon={FlaskConical} status={status} label="E2E tests" hint={hint}>
        <span className={cn(statusTextClass[status], "tabular-nums")}>{run?.status ?? "—"}</span>
      </StatusTrigger>
      <PopoverContent align="end" className="w-80 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-2">
            {status === "fail" ? <CircleX className={cn("mt-0.5 size-4", statusTextClass.fail)} /> : <FlaskConical className="mt-0.5 size-4 text-muted-foreground" />}
            <div>
              <p className="text-sm font-semibold">Playwright E2E</p>
              <p className={cn("mt-0.5", statusTextClass[status])}>{label}</p>
            </div>
          </div>
          {run?.web_url && <a href={run.web_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-muted-foreground hover:text-foreground">Run <ExternalLink className="size-3" /></a>}
        </div>
        {run && (
          <>
            <Separator />
            <div className="space-y-2 text-xs">
              <DetailRow icon={GitBranch} label="Branch"><span className="font-mono">{run.branch_name}</span></DetailRow>
              {run.build_number && <DetailRow icon={GitCommitHorizontal} label="Build"><span className="font-mono">#{run.build_number}</span></DetailRow>}
              {run.running || run.status === "QUEUED" ? <DetailRow icon={Clock} label="Status">{run.status === "QUEUED" ? "Waiting for the deployment chain" : formatDuration(run.seconds_remaining) ? `~${formatDuration(run.seconds_remaining)} remaining` : "Running"}</DetailRow> : run.finish_date && <DetailRow icon={Clock} label="Last run">{dayjs(run.finish_date, "YYYYMMDDTHHmmssZ").fromNow()}</DetailRow>}
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}

export function DeploymentStatusBar() {
  const shouldShow = import.meta.env.VITE_ENVIRONMENT === "preprod";
  const frontendQuery = useQuery({
    queryKey: [QueryKeys.deploymentStatus, "hub_frontend"],
    queryFn: () => fetchDeploymentStatus("hub_frontend"),
    enabled: shouldShow,
    refetchInterval: (query) => {
      const data = query.state.data;
      return data?.running_build || data?.e2e_run?.running || data?.e2e_run?.status === "QUEUED" ? 20_000 : 300_000;
    },
    staleTime: 240_000,
  });
  const backendQuery = useQuery({
    queryKey: [QueryKeys.deploymentStatus, "hub_backend_api"],
    queryFn: () => fetchDeploymentStatus("hub_backend_api"),
    enabled: shouldShow,
    refetchInterval: (query) => query.state.data?.running_build ? 20_000 : 300_000,
    staleTime: 240_000,
  });

  if (!shouldShow || frontendQuery.isPending || backendQuery.isPending || frontendQuery.isError || backendQuery.isError) return null;

  return (
    <div className="flex items-center">
      <DeploymentPopover deployment={frontendQuery.data} title="Frontend" icon={AppWindow} />
      <DeploymentPopover deployment={backendQuery.data} title="Backend API" icon={Server} />
      <E2EPopover run={frontendQuery.data.e2e_run} />
    </div>
  );
}
